import React, { useCallback } from "react";
import { DYNAMIC_ISLAND } from "../../config/dynamicIsland.configuration";
import DynamicIsland from "./DynamicIsland";
import { type DynamicIslandHostContext } from "./dynamicIsland.registry";
import { type DynamicIslandPayload } from "./dynamicIsland.types";

/** Props for the layout slot mounted in FloatingMenu. */
export interface DynamicIslandSlotProps {

    /** Active payload, if any. */
    "payload": DynamicIslandPayload | undefined;

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Host capabilities forwarded to variant content (camera, future panel APIs, …). */
    "hostContext": DynamicIslandHostContext;

    /**
     * Notified when the FAB row should keep center column spacing.
     * True while the island is open or an exit animation is in progress.
     */
    "onLayoutOccupancyChange"?: ( occupied: boolean ) => void;
}

/**
 * Inline slot for the Dynamic Island on the FAB row, centered between
 * the GPS FAB and the grouped burst main button.
 *
 * @param props Slot props.
 * @returns Island slot or null when disabled.
 */
const DynamicIslandSlot: React.FC<DynamicIslandSlotProps> = ( {
    payload,
    isDarkUi,
    hostContext,
    onLayoutOccupancyChange,
} ) => {
    const handleLayoutOccupancyChange = useCallback( ( occupied: boolean ): void => {
        onLayoutOccupancyChange?.( occupied );
    }, [ onLayoutOccupancyChange ] );

    if ( !DYNAMIC_ISLAND.enabled ) {
        return null;
    }

    return (
        <div className="w-full min-w-0 flex items-center justify-center pointer-events-none">
            <DynamicIsland
                hostContext={hostContext}
                isDarkUi={isDarkUi}
                onLayoutOccupancyChange={handleLayoutOccupancyChange}
                payload={payload}
            />
        </div>
    );
};

export default DynamicIslandSlot;
